import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import axios from 'axios';
import { toast } from 'sonner';
import { isNetworkError, showApiErrorToast } from '@/utils/errors';
import { useAuthStore } from '@/store/auth.store';
import { DEFAULT_MAP_ON } from '@/features/moTravel/constants/moTravel.constants';
import { getBangladeshToday } from '@/features/moTravel/utils/bangladeshClock';
import { ssoLogin } from '../api/auth.api';
import { SITE_VALUES } from '../constants/auth.constants';
import type { Site } from '../types/auth.types';

const EXPIRED_MESSAGE = 'This sign-in link has expired. Please log in again.';

const LANDING_ROUTE = '/geo-location/mo-travel-history';

function takeHandoverFromHash(): { token: string; site: Site } | null {
  const { hash, pathname, search } = window.location;

  if (!hash) {
    return null;
  }

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get('token');
  const site = params.get('site');
  window.history.replaceState(null, '', `${pathname}${search}`);

  if (token === null) {
    return null;
  }

  return { token, site: SITE_VALUES.find((known) => known === site) ?? 'general' };
}

export function useSsoLogin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    const sendToLogin = (): void => {
      useAuthStore.getState().clearAuth();
      queryClient.clear();
      void navigate({ to: '/login', replace: true });
    };

    async function signIn(): Promise<void> {
      const handover = takeHandoverFromHash();

      if (handover === null) {
        toast.error(EXPIRED_MESSAGE);
        sendToLogin();
        return;
      }

      try {
        await ssoLogin(handover.token, handover.site);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          toast.error(EXPIRED_MESSAGE);
        } else if (isNetworkError(error)) {
          toast.error('Could not reach the server. Please try the link again.');
        } else {
          showApiErrorToast(error, 'Could not sign you in');
        }

        sendToLogin();
        return;
      }

      queryClient.clear();
      toast.success('Login successful');
      void navigate({
        to: LANDING_ROUTE,
        search: { date: getBangladeshToday(), mapOn: DEFAULT_MAP_ON },
        replace: true,
      });
    }

    void signIn();
  }, [navigate, queryClient]);
}
